<template>
  <div class="ticket-card" :class="{ 'is-selected': qty > 0 }">
    <div
      class="ticket-row"
      @mouseenter="$emit('detail', { type: 'ticket', id: ticket.id })"
      @focusin="$emit('detail', { type: 'ticket', id: ticket.id })"
    >
      <div class="ticket-info">
        <div class="ticket-name">{{ ticket.name }}</div>
        <!-- Phones have no side panel (and no hover): the first two "What's
             included" bullets stay visible, the rest + description open inline.
             Hidden on desktop via CSS. -->
        <ul v-if="includesPreview.length" class="ticket-includes ticket-includes--preview">
          <li v-for="(inc, i) in includesPreview" :key="i">{{ inc }}</li>
        </ul>
        <!-- Phones: the description's first line is always visible; the full
             text opens in the disclosure below. Hidden on wider screens. -->
        <div
          v-if="descText && !isOpen('ticket')"
          ref="descPreview"
          class="ticket-desc ticket-desc--preview"
        >{{ descText }}</div>
        <!-- Opens between the preview bullets and the toggle ("read more"
             pattern), so the list continues where it was cut off. -->
        <div
          v-if="ticketHasMore"
          :id="panelId('ticket')"
          class="disclosure"
          :class="{ 'is-open': isOpen('ticket') }"
        >
          <div class="disclosure-inner">
            <ul v-if="includesRest.length" class="ticket-includes ticket-includes--rest">
              <li v-for="(inc, i) in includesRest" :key="i">{{ inc }}</li>
            </ul>
            <div v-if="ticket.desc" class="ticket-desc" v-html="ticket.desc"></div>
          </div>
        </div>
        <button
          v-if="ticketHasMore"
          type="button"
          class="details-toggle"
          :class="{ 'details-toggle--no-more': !phoneHasMore }"
          :aria-expanded="String(isOpen('ticket'))"
          :aria-controls="panelId('ticket')"
          @click="toggle('ticket')"
        >
          Details
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg>
        </button>
      </div>
      <!-- Add-on-only tickets have no meaningful price of their own; the column
           stays so the quantity stepper keeps its position. -->
      <div class="ticket-pricing">
        <div v-if="!ticket.hidePrice" class="ticket-price">{{ currencySymbol }}{{ ticket.price.toLocaleString() }}</div>
      </div>
      <lp-qty-stepper
        :value="qty"
        :max="ticket.max"
        @input="$emit('set-ticket', { id: ticket.id, qty: $event })"
      />
    </div>

    <!-- Early-bird / late-registration notice. Full-width strip below the row
         so the price column stays clean; text is the server-built
         special_price_description (keeps the original wording, incl. <br>). -->
    <div v-if="ticket.priceSub" class="ticket-notice">
      <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
      <span v-html="ticket.priceSub"></span>
    </div>

    <div
      v-if="ticket.addons && ticket.addons.length"
      class="addon-toggle"
      :class="{ open }"
    >
      <div class="addon-header" @click="$emit('toggle-addons', ticket.id)">
        Add-ons available ({{ ticket.addons.length }})
        <svg aria-hidden="true" class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg>
      </div>
      <div class="addon-body">
        <div
          v-for="a in ticket.addons"
          :key="a.id"
          class="addon-row"
          :class="{ 'is-selected': (addonQty[ticket.id + ':' + a.id] || 0) > 0 }"
          @mouseenter="$emit('detail', { type: 'addon', id: a.id })"
          @focusin="$emit('detail', { type: 'addon', id: a.id })"
        >
          <div>
            <div class="name">{{ a.name }}</div>
            <button
              v-if="a.desc"
              type="button"
              class="details-toggle"
              :aria-expanded="String(isOpen(a.id))"
              :aria-controls="panelId(a.id)"
              @click="toggle(a.id)"
            >
              Details
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg>
            </button>
          </div>
          <div class="price">{{ currencySymbol }}{{ a.price }}</div>
          <lp-qty-stepper
            :value="addonQty[ticket.id + ':' + a.id] || 0"
            :max="a.max"
            @input="$emit('set-addon', { ticketId: ticket.id, id: a.id, qty: $event })"
          />
          <div
            v-if="a.desc"
            :id="panelId(a.id)"
            class="disclosure addon-disclosure"
            :class="{ 'is-open': isOpen(a.id) }"
          >
            <div class="disclosure-inner">
              <div class="addon-desc">{{ a.desc }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'lp-ticket-card',
  props: {
    ticket: { type: Object, required: true },
    qty: { type: Number, default: 0 },
    addonsLib: { type: Object, default: () => ({}) },
    addonQty: { type: Object, default: () => ({}) },
    open: { type: Boolean, default: false },
    currencySymbol: { type: String, default: '$' },
  },
  data() {
    return {
      // Inline disclosures that are open: 'ticket' or an addon id.
      openPanels: [],
      // The one-line phone description preview is cut off.
      descOverflows: false,
    }
  },
  computed: {
    includes() {
      return this.ticket.includes || []
    },
    includesPreview() {
      return this.includes.slice(0, 2)
    },
    includesRest() {
      return this.includes.slice(2)
    },
    ticketHasMore() {
      return this.includesRest.length > 0 || !!this.ticket.desc
    },
    // Plain text for the phone preview; DOMParser keeps the server HTML inert.
    descText() {
      if (!this.ticket.desc) return ''
      const doc = new DOMParser().parseFromString(this.ticket.desc, 'text/html')
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
    },
    // Phones show the description's first line inline, so Details is only
    // needed when there is more than that.
    phoneHasMore() {
      return this.includesRest.length > 0 || this.descOverflows || this.isOpen('ticket')
    },
  },
  watch: {
    descText() {
      this.$nextTick(this.measureDesc)
    },
  },
  mounted() {
    this.measureDesc()
    if (typeof ResizeObserver !== 'undefined') {
      this._descObserver = new ResizeObserver(() => this.measureDesc())
      this._descObserver.observe(this.$el)
    }
  },
  beforeDestroy() {
    if (this._descObserver) this._descObserver.disconnect()
  },
  methods: {
    isOpen(key) {
      return this.openPanels.includes(key)
    },
    toggle(key) {
      const i = this.openPanels.indexOf(key)
      if (i >= 0) this.openPanels.splice(i, 1)
      else this.openPanels.push(key)
    },
    // Clamped to one line on phones; hidden elsewhere, where it measures 0.
    measureDesc() {
      const el = this.$refs.descPreview
      if (el) this.descOverflows = el.scrollHeight > el.clientHeight + 1
    },
    panelId(key) {
      return `ticket-${this.ticket.id}-details-${key}`
    },
  },
}
</script>

<style lang="scss">
.ticket-card {
  padding: 18px 20px;
  border-bottom: 0.5px solid var(--border);
  border-left: 3px solid transparent;
  transition: background 0.12s, border-left-color 0.12s;
  cursor: default;
}
.ticket-card:last-child { border-bottom: none; }
.ticket-card:hover {
  background: var(--bg);
  border-left-color: var(--brand);
}
.ticket-card:has(.qty-stepper .val:not(:empty)) {
  /* placeholder for future "selected" styling */
}
.ticket-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 16px;
  align-items: center;
}
.ticket-name {
  font-size: var(--fs-body-lg);
  font-weight: 500;
  line-height: 1.35;
}
.ticket-pricing { display: flex; flex-direction: column; align-items: flex-end; }
.ticket-price {
  font-size: var(--fs-lead);
  font-weight: 500;
}
.ticket-notice {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: var(--radius-sm);
  background: rgba(184, 134, 11, 0.10);
  color: var(--warning);
  font-size: var(--fs-label);
  line-height: 1.4;
}
.ticket-notice svg { flex-shrink: 0; }
/* Emphasize the two scannable facts (deadline date + new price), wrapped in
   <strong> by the server copy. */
.ticket-notice strong { font-weight: 600; }
/* The server-built copy hard-codes <br> between the two sentences; keep the
   notice on one line here (the space after each <br> preserves word spacing). */
.ticket-notice br { display: none; }
.addon-toggle {
  margin-top: 12px;
  background: var(--surface-alt);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.addon-header {
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: var(--fs-small);
  font-weight: 500;
  color: var(--brand-deep);
  user-select: none;
}
.addon-header:hover { background: var(--surface); }
.addon-header .chev { margin-left: auto; transition: transform 0.2s; }
.addon-toggle.open .addon-header .chev { transform: rotate(180deg); }
.addon-body {
  display: none;
  /* Rows carry their own 12px padding; this just keeps the hover highlight
     off the header border and the card edge. */
  padding: 6px 14px;
  border-top: 0.5px solid var(--border);
  background: var(--surface);
}
.addon-toggle.open .addon-body { display: block; }
.addon-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  padding: 12px 8px;
  margin: 0 -8px;
  align-items: center;
  border-bottom: 0.5px dashed var(--border);
  border-left: 2px solid transparent;
  border-radius: var(--radius-sm);
  transition: background 0.12s, border-left-color 0.12s;
}
.addon-row:last-child { border-bottom: none; }
.addon-row:hover {
  background: var(--bg);
  border-left-color: var(--brand-light);
}
.addon-row .name { font-size: var(--fs-small); font-weight: 500; }
.addon-row .price { font-size: var(--fs-body); font-weight: 500; }

/* ---- Phone details (≤1100px, where .tickets-area stacks and the side
   panel is hidden). Desktop keeps the hover panel. ---- */
.details-toggle {
  position: relative;
  display: none;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 0;
  background: none;
  border: 0;
  font: inherit;
  font-size: var(--fs-label);
  font-weight: 500;
  color: var(--brand-deep);
  cursor: pointer;
}
/* Invisible hit area so the small text link meets the 44px touch target. */
.details-toggle::after {
  content: '';
  position: absolute;
  inset: -12px -10px;
}
.details-toggle svg { transition: transform 0.2s; }
.details-toggle[aria-expanded="true"] svg { transform: rotate(180deg); }

.ticket-includes {
  margin: 6px 0 0;
  padding-left: 18px;
  font-size: var(--fs-small);
  line-height: 1.6;
  color: var(--text-secondary);
}
.ticket-includes li + li { margin-top: 2px; }
.ticket-includes--preview { display: none; }
.ticket-desc--preview { display: none; }

/* Height animation without JS: the 0fr → 1fr row transitions smoothly, and
   visibility drops collapsed content from the tab order / a11y tree (it flips
   to hidden only after the close transition ends). */
.disclosure {
  display: none;
  grid-template-rows: 0fr;
  visibility: hidden;
  transition: grid-template-rows 0.2s ease, visibility 0.2s;
}
.disclosure.is-open { grid-template-rows: 1fr; visibility: visible; }
.disclosure-inner { min-height: 0; overflow: hidden; }

/* Continues the preview list seamlessly (same spacing as li + li). */
.ticket-includes--preview + .disclosure .ticket-includes--rest { margin-top: 2px; }
/* Plain text, no tinted box: a hovered/tapped card uses the same --bg tint,
   which made the box invisible and left only a stray indent. */
.ticket-desc {
  margin-top: 8px;
  font-size: var(--fs-small);
  line-height: 1.6;
  color: var(--text-secondary);
}
.ticket-desc > :first-child { margin-top: 0; }
.ticket-desc > :last-child { margin-bottom: 0; }

/* The addon row is a grid: pull the collapsed disclosure up over the row gap
   and give the gap back inside, so a closed row keeps its height. */
.addon-disclosure { grid-column: 1 / -1; margin-top: -12px; }
.addon-desc {
  margin-top: 8px;
  font-size: var(--fs-label);
  line-height: 1.6;
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

@media (max-width: 1100px) {
  .details-toggle { display: inline-flex; }
  .ticket-includes--preview { display: block; }
  .disclosure { display: grid; }
}
@media (prefers-reduced-motion: reduce) {
  .details-toggle svg,
  .disclosure { transition: none; }
}

@media (max-width: 800px) {
  .ticket-row { grid-template-columns: 1fr; }
  .ticket-pricing { align-items: flex-start; }
}

/* Phones: standalone cards as in templates/mobile-ticket-picker.html —
   name + price on top, details link + stepper on the bottom row, add-ons as
   their own small cards. The info column is flattened (display: contents) so
   its children can sit in the card grid without changing the markup. */
@media (max-width: 640px) {
  .ticket-card,
  .ticket-card:last-child {
    padding: 14px;
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: 14px;
    transition: border-color 0.15s, background 0.15s;
  }
  .ticket-card:hover { background: var(--surface); border-left-color: var(--border); }
  .ticket-card.is-selected,
  .ticket-card.is-selected:hover {
    border-color: var(--brand);
    background: var(--surface);
    background: color-mix(in srgb, var(--brand-soft) 30%, var(--surface));
  }

  .ticket-row {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "name price"
      "prev prev"
      "dprev dprev"
      "disc disc"
      "actions actions";
    /* Min gap between the title and the price. */
    gap: 0 16px;
  }
  .ticket-info { display: contents; }
  .ticket-name {
    grid-area: name;
    align-self: start;
    font-size: var(--fs-body-lg);
    font-weight: 600;
    line-height: 1.3;
  }
  .ticket-pricing { grid-area: price; align-self: start; align-items: flex-end; }
  .ticket-price { font-size: var(--fs-lead); font-weight: 700; white-space: nowrap; }
  .ticket-info > .ticket-includes--preview { grid-area: prev; margin-top: 8px; }
  .ticket-info > .disclosure { grid-area: disc; }
  .ticket-info > .ticket-desc--preview {
    grid-area: dprev;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .ticket-info > .details-toggle--no-more { display: none; }
  .ticket-info > .details-toggle {
    /* Details and the stepper share one full-width row (opposite ends), so the
       wide stepper doesn't widen the price column and squeeze the title. */
    grid-area: actions;
    justify-self: start;
    align-self: center;
    margin-top: 8px;
    font-size: var(--fs-small);
    font-weight: 600;
    color: var(--brand);
  }
  .ticket-row > .qty-stepper { grid-area: actions; justify-self: end; margin-top: 8px; }

  .ticket-card .qty-stepper {
    border: 1px solid var(--border-strong);
    border-radius: 10px;
  }
  .ticket-card .qty-stepper button { width: 44px; height: 44px; color: var(--brand); }
  .ticket-card .qty-stepper button:disabled { color: var(--text-tertiary); }
  .ticket-card .qty-stepper .val {
    min-width: 28px;
    height: 44px;
    padding: 0 2px;
    border: 0;
    font-size: var(--fs-body-lg);
    font-weight: 600;
  }
  .ticket-card .qty-stepper button:focus-visible { outline: 2px solid var(--brand); outline-offset: -2px; }

  .ticket-notice { margin-top: 10px; }

  /* Add-ons: shown only once this ticket is picked, always expanded — a
     divider + eyebrow heading over small cards. The header stays in the DOM
     but is inert here (the open/closed state only matters on desktop). */
  .ticket-card:not(.is-selected) .addon-toggle { display: none; }
  .addon-toggle {
    margin-top: 12px;
    padding-top: 10px;
    background: none;
    border: 0;
    border-top: 1px solid var(--border);
    border-radius: 0;
    overflow: visible;
  }
  .addon-header {
    padding: 0;
    min-height: 32px;
    font-size: var(--fs-caption);
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-secondary);
  }
  .addon-header { pointer-events: none; cursor: default; }
  .addon-header:hover { background: none; }
  .addon-header .chev { display: none; }
  .addon-body {
    padding: 6px 0 0;
    border-top: 0;
    background: none;
  }
  .addon-toggle .addon-body { display: flex; flex-direction: column; gap: 6px; }
  .addon-row,
  .addon-row:last-child {
    gap: 0 10px;
    margin: 0;
    padding: 10px;
    min-height: 48px;
    background: var(--surface);
    border: 1px solid var(--border-strong);
    border-radius: 10px;
  }
  .addon-row:hover { background: var(--surface); border-left-color: var(--border-strong); }
  .addon-row.is-selected,
  .addon-row.is-selected:hover {
    border-color: var(--brand);
    background: var(--surface);
    background: color-mix(in srgb, var(--brand-soft) 45%, var(--surface));
  }
  /* No row gap above, so no pull-up needed (a 0-height grid item can't
     shrink its track below zero, which left a 12px strip under each card). */
  .addon-disclosure { margin-top: 0; }
  .addon-row .name { font-size: var(--fs-small); font-weight: 400; line-height: 1.35; }
  .addon-row .price { font-size: var(--fs-small); font-weight: 600; white-space: nowrap; }
  .addon-row .price::before { content: '+'; }
  /* Details becomes a chevron after the name; its hit area (::after) is
     stretched over the whole card, so tapping the add-on toggles the
     description. Still a real button: keyboard + aria-expanded keep working,
     and the "Details" text stays for screen readers (font-size 0). */
  .addon-row { position: relative; }
  .addon-row > :first-child { display: flex; align-items: flex-start; gap: 6px; }
  .addon-row .details-toggle {
    position: static;
    flex: none;
    margin-top: 2px;
    padding: 0;
    font-size: 0;
    color: var(--text-secondary);
  }
  .addon-row .details-toggle svg { width: 14px; height: 14px; }
  .addon-row .details-toggle::after { inset: 0; border-radius: 10px; }
  .addon-row > .qty-stepper { position: relative; z-index: 1; }
  /* Template order: control on the left (the stepper stands in for the
     checkbox), then name + details, price at the right edge. */
  .addon-row { grid-template-columns: auto minmax(0, 1fr) auto; }
  .addon-row > .qty-stepper { grid-column: 1; grid-row: 1; align-self: center; }
  .addon-row > :first-child { grid-column: 2; grid-row: 1; }
  .addon-row > .price { grid-column: 3; grid-row: 1; align-self: center; }
  .addon-row > .addon-disclosure { grid-column: 1 / -1; }
  .addon-row .qty-stepper { border-radius: 8px; }
  .addon-row .qty-stepper button { width: 30px; height: 28px; font-size: var(--fs-body); }
  .addon-row .qty-stepper .val { height: 28px; min-width: 20px; font-size: var(--fs-small); }
}
</style>
